import { defineStore } from 'pinia'
import { tnApi } from '@/api'
import { ref, computed } from 'vue'
import path from 'node:path'
import { modalForMedia } from '@/util/dialogs.js'
import { isNumber } from 'lodash'
import { v4 as uuidv4 } from 'uuid'
import { useNotificationStore } from '@/pinia/NotificationStore'
import { useEntityStore } from '@/pinia/EntityStore'

interface Media {
  id: number
  blurhash: string
  org_name: string
  // add any other properties here
}

export const useMediaStore = defineStore('media', () => {
  // const store = useMediaStore()
  const notificationStore = useNotificationStore()
  const entityStore = useEntityStore()

  const mediaFile = ref(null)

  const mediaFiles = ref<Media[]>([]) // specify the type of the objects in the array
  const mediaFolders = ref<object[]>([])

  const newMediaFiles = ref<object[]>([])
  const mediaUploads = ref<string[]>([])

  const selectedItems = ref<number[]>([])
  const currentItem = ref<number>(0)
  const newFileIds = ref<number[]>([])

  const totalMediaCount = ref<number>(0)

  const progressId = ref<string | null>(null)

  const isLoading = ref(false)
  const isLoadingItem = ref(false)
  const isSaving = ref(false)

  const currentMedia = computed(() => {
    return mediaFiles.value.find(item => item.id === currentItem.value)
  })

  const currentBlurHash = computed(() => currentMedia.value ? currentMedia.value.blurhash : null)
  interface IPickerOpions {
    multiple?: boolean,
    folder?: string,
    parent: string,
    parentId: number,
    upload: boolean,
    title?: string,
  }

  const PickerOptionDefaults = {
    folder: '',
    multiple: false,
    upload: true,
    allowedTypes: ['image']
  }

  const store = useMediaStore()

  const picker = async (options: IPickerOpions) => {
    const params = Object.assign({}, PickerOptionDefaults, options)

    newMediaFiles.value = []

    if (!params.parentId || !params.parent) {
      throw new Error('Parent and parentId are required')
    }

    if (params.parentId === -1) {
      params.parentId = 0
    }

    if (!params.title) {
      const verb = params.upload ? 'auswählen oder hochladen' : 'auswählen'
      let type: string = params.multiple ? 'Medien' : 'Medium'
      if (params.allowedTypes.length === 1) {
        switch (params.allowedTypes[0]) {
          case 'image':
            type = params.multiple ? 'Bilder' : 'Bild'
            break
          case 'video':
            type = params.multiple ? 'Videos' : 'Video'
            break
        }
      }
      params.title = `${type} ${verb}`
    }

    let ids: number | number[] | boolean | null = await modalForMedia(params)

    if (ids === null || ids === false) {
      return null
    }

    if (typeof ids === 'number') {
      ids = [ids]
    }

    if (Array.isArray(ids) && ids.length) {
      const array: number[] = ids
      const items = mediaFiles.value.filter(item => array.includes(item.id))
      if (Array.isArray(items) && items.length) {
        if (params.multiple) {
          return items
        }
        return items[0]
      }
    }
    return []
  }
  const load = async (parent: string, parentId: number) => {
    if (parent === undefined || parentId === undefined) {
      return
    }
    console.log(parent, parentId)
    isLoading.value = true
    const {
      mediaLoad
    } = tnApi(true)
    const { media, folders, mediaTotal } = await mediaLoad(parent, parentId)
    mediaFiles.value = media
    totalMediaCount.value = mediaTotal
    mediaFolders.value = folders
    isLoading.value = false
  }

  const loadByFolder = async (p1: string = '', p2: string = '', p3: string = '', p4: string = '', p5: string = '') => {
    let folder = path.join(p1, p2, p3, p4, p5)
    if (folder === '.') {
      folder = ''
    }

    isLoading.value = true
    const {
      mediaLoad
    } = tnApi(true)
    const { media, folders } = await mediaLoad(folder)
    mediaFiles.value = media
    mediaFolders.value = folders
    isLoading.value = false
  }

  const find = async (id: number) => {
    if (!id) return
    isLoadingItem.value = true
    const {
      mediaFind
    } = tnApi(true)
    const { media } = await mediaFind(id)
    mediaFile.value = media
    isLoadingItem.value = false
    return media
  }

  const upload = async (id: string, formData: any, params: any) => {
    if (!mediaUploads.value.includes(id)) {
      mediaUploads.value.push(id)
      try {
        const {
          mediaUpload
        } = tnApi(true)
        const { media } = await mediaUpload(formData, params)

        store.$patch((state) => {
          state.mediaFiles.unshift(media)
        })
        return Promise.resolve(true)
      } catch (error) {
        return Promise.reject(error)
      }
    }
  }

  const setCurrentItem = (id: number) => {
    currentItem.value = id
  }

  const addItem = (id: number) => {
    if (!selectedItems.value.includes(id)) {
      selectedItems.value.push(id)
    }
  }

  const migrationProgress = (payload: any) => {
    console.log(payload, progressId.value)
    if (progressId.value === null) {
      progressId.value = uuidv4()
      notificationStore.addProgressNotification(progressId.value, `Medien für ${payload.info} werden migriert`, payload.total, payload.current)
    } else {
      notificationStore.updateProgressNotification(progressId.value, payload.current, payload.total)
    }
  }

  const closeMigrationProgress = async (id: string) => {
    const progress = notificationStore.getProgressById(progressId.value)
    if (progress) {
      progress.close()
      progressId.value = null
      await entityStore.find(id)
    }
  }

  const addNewFile = (file) => {
    const index = newMediaFiles.value.findIndex(item => item.id === file.id)
    if (index === -1) {
      newMediaFiles.value.push(file)
    }
  }

  const addMediaAfterUpload = async (id: number) => {
    newFileIds.value.push(id)
    const media = await find(id)
    newMediaFiles.value = newMediaFiles.value.filter(item => item.file.name !== media.org_name)

    const index = mediaFiles.value.findIndex(item => item.id === id)
    store.$patch((state) => {
      state.mediaFiles[index] = media
    })
  }

  const updateSelectedItems = (ids: number | number[]) => {
    if (isNumber(ids)) {
      ids = [ids]
    }

    selectedItems.value = ids
  }

  const removeItem = (id: number) => {
    return selectedItems.value.filter(item => item !== id)
  }

  return {
    find,
    load,
    picker,
    upload,

    addNewFile,
    newMediaFiles,
    mediaFile,
    mediaFiles,

    currentItem,
    currentBlurHash,
    currentMedia,
    newFileIds,
    mediaUploads,
    migrationProgress,
    closeMigrationProgress,

    addItem,
    removeItem,
    updateSelectedItems,
    addMediaAfterUpload,

    totalMediaCount,
    selectedItems,
    setCurrentItem,

    isLoading,
    isLoadingItem,
    isSaving
  }
})
